import React, { useState, useCallback } from 'react';
import { Copy, Check } from 'lucide-react';
import {
  ArrowRight,
  User,
  Star,
  ShoppingCart,
  Diamond,
  Heart,
  TrendingUp,
  HandHeart,
  Layout,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { Collector } from '@/types/collector';
import { DynamicIcon } from '@/components/ui/shared/dynamic-icon';
import { hexToRgba } from '@/lib/utils/color-utils';

interface CollectorsCardProps {
  collector: Collector;
  rank?: number;
  showRank?: boolean;
  showDetails?: boolean;
  removeBorders?: boolean;
  hideIds?: boolean;
  expandable?: boolean;
}

export function CollectorsCard({
  collector,
  rank,
  showRank = false,
  showDetails = false,
  removeBorders = false,
  hideIds = false,
  expandable = false,
}: CollectorsCardProps) {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);

  const toggleExpand = () => {
    if (expandable) {
      setExpanded(!expanded);
    }
  };

  const copyToClipboard = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation(); // Prevent expanding when clicking on the copy button
      navigator.clipboard
        .writeText(collector.mainWalletAddress)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(err => {
          console.error('Failed to copy wallet address:', err);
        });
    },
    [collector.mainWalletAddress]
  );

  const nameNormal = (name: string) => {
    name = name.replace('temp_', '');
    name = name.replace('TEMP_', '');
    return name;
  };

  return (
    <div
      className={`${removeBorders ? '' : 'border border-zinc-200'} rounded-md ${removeBorders ? 'py-2 px-0' : 'p-4'} ${removeBorders ? '' : 'hover:border-zinc-800'} transition-all duration-300 ${removeBorders ? '' : 'hover:shadow-xs'} ${expandable ? 'cursor-pointer' : ''}`}
      onClick={expandable ? toggleExpand : undefined}
    >
      <div className="flex items-start">
        {/* Rank */}
        {showRank && rank && (
          <div className="flex items-center justify-center w-6 h-6 bg-zinc-100 font-mono text-xs mr-3 rounded">
            {rank}
          </div>
        )}

        {/* Collector Info */}
        <div className="flex-1">
          <div className="flex items-start justify-between">
            <div className="flex items-center">
              <div>
                <h4 className="text-[10px] font-medium uppercase tracking-wide flex items-center">
                  <span>
                    {hideIds ? '' : `ID: ${collector.id} - `}
                    {nameNormal(collector.name || collector.username || '')}
                  </span>
                  <div className="ml-2 text-zinc-500 font-mono relative group flex items-center">
                    <span
                      className="cursor-pointer hover:text-zinc-800 transition-colors flex items-center"
                      onClick={copyToClipboard}
                    >
                      ({collector.mainWalletAddress.substring(0, 6)}...
                      {collector.mainWalletAddress.substring(collector.mainWalletAddress.length - 4)})
                      {copied ? (
                        <Check className="ml-1 h-3 w-3 text-green-500" />
                      ) : (
                        <Copy className="ml-1 h-3 w-3 opacity-0 group-hover:opacity-100 transition-opacity" />
                      )}
                    </span>
                    <span className="absolute -top-6 left-1/2 transform -translate-x-1/2 bg-black text-white text-[8px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
                      {copied ? 'Copied!' : 'Click to copy'}
                    </span>
                  </div>
                </h4>
              </div>
              {expandable && (
                <div className="ml-1 text-zinc-400">
                  {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
                </div>
              )}
            </div>

            {/* Tier Badge */}
            {!collector.tiers || typeof collector.tiers === 'number' ? null : (
              <div
                className={`px-2 py-0.5 text-[8px] font-mono rounded border`}
                style={{
                  borderColor: collector.tiers.color,
                  color: collector.tiers.color,
                  backgroundColor: hexToRgba(collector.tiers.color, 0.07),
                }}
              >
                {collector.tiers.title}
              </div>
            )}
          </div>

          {/* Expandable content */}
          {(!expandable || expanded) && (
            <>
              {/* Badges */}
              <div className="flex flex-wrap gap-1 mt-2 select-none">
                {collector.badges.slice(0, 3).map(badge => {
                  if (!badge || typeof badge === 'number') return null;

                  return (
                    <div
                      key={badge.id}
                      className="flex items-center bg-zinc-100 rounded px-1.5 py-0.5"
                      title={badge.title}
                      style={{ backgroundColor: hexToRgba(badge.color, 0.15) }}
                    >
                      <span className="flex items-center justify-center mr-1">
                        <DynamicIcon icon={badge?.iconSelector} className="h-3 w-3" />
                      </span>
                      <span className="text-[8px] font-mono">{badge.title}</span>
                    </div>
                  );
                })}

                {collector.badges.length > 3 && (
                  <div className="flex items-center bg-zinc-100 rounded px-1.5 py-0.5">
                    <span className="text-[8px] font-mono text-zinc-800">+{collector.badges.length - 3} more</span>
                  </div>
                )}
              </div>

              {/* Stats */}
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-zinc-100">
                <div className="grid grid-cols-4 gap-3 w-full">
                  <div>
                    <p className="text-[8px] text-zinc-500">ARTWORK HOLDINGS</p>
                    <p className="text-xs">{collector.tradingStats.totalOwned}</p>
                  </div>
                  <div>
                    <p className="text-[8px] text-zinc-500">VOLUME</p>
                    <p className="text-xs">${collector.tradingStats.tradedVolume}</p>
                  </div>
                  <div>
                    <p className="text-[8px] text-zinc-500">COLLECTION DIVERSITY</p>
                    <p className="text-xs">{collector.tradingStats.totalCollection}</p>
                  </div>
                  <div>
                    <p className="text-[8px] text-zinc-500">COLLECTOR SINCE</p>
                    <p className="text-xs">{new Date(collector.tradingStats.firstPurchaseAt).getFullYear()}</p>
                  </div>
                </div>

                {showDetails && (
                  <button className="flex items-center space-x-1 text-[8px] uppercase px-2 py-1 bg-zinc-100 rounded-md hover:bg-white hover:border hover:border-zinc-800 transition-all duration-300 group">
                    <span>Details</span>
                    <ArrowRight
                      size={8}
                      className="transform group-hover:translate-x-0.5 transition-transform duration-300"
                    />
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
